import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import AppRoutes from './common/AppRoutes';
import Layout from './components/Layout';
import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import { MaterialUIControllerProvider } from 'otis-theme/context';
import theme from 'otis-theme/assets/theme';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClientProvider, QueryClient } from 'react-query';
import NotFound from './pages/NotFound';
import { PromptProvider } from './components/AppPrompt';
import { AppContextProvider } from 'common/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getRollbarConfig } from '../src/services/rollbarConfig';
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { ROUTES } from 'common/constants/urls';

const rollbarConfig = getRollbarConfig();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// v11 sizes both the container and each toast from --toastify-toast-width, so the width is set as a CSS variable.
const toastStyles = {
  fontSize: theme.typography.button.fontSize,
  '--toastify-toast-width': '400px',
} as React.CSSProperties;

// Import if you're using prop-types
import EnvBanner from './components/common/EnvBanner/EnvBanner';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import CustomDragLayer from './common/CustomDragLayer';
import { loadHotjar, triggerHotjarStateChange } from './utils/loadHotjar';
import { initMixpanel } from './services/analyticsService';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LinearProgress sx={{ width: '-webkit-fill-available' }} />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to='/login' />;
};

interface RequireRolesProps {
  children: React.ReactNode;
  requiredRoles: string[];
}

const RequireRoles: React.FC<RequireRolesProps> = ({ children, requiredRoles }) => {
  const { isLoading, hasRole } = useAuth();

  if (isLoading) {
    return <LinearProgress sx={{ width: '-webkit-fill-available' }} />;
  }

  const isAuthorized = requiredRoles.every(role => hasRole(role));
  return isAuthorized ? <>{children}</> : <Navigate to={ROUTES.HOME} replace />;
};

const App = () => {
  const rollbarConfig = getRollbarConfig();
  const location = useLocation();

  useEffect(() => {
    initMixpanel();
    loadHotjar();
  }, []);

  // Notify Hotjar of SPA route changes so funnels/heatmaps see the correct URL
  useEffect(() => {
    const virtualPath = location.pathname + location.search;
    triggerHotjarStateChange(virtualPath);
  }, [location.pathname, location.search]);

  const appContent = (
    <AppContextProvider>
      <DndProvider backend={HTML5Backend}>
        <MaterialUIControllerProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <EnvBanner />
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <PromptProvider>
                  <CustomDragLayer />
                  <Routes>
                    {AppRoutes.map((route, index) => {
                      const { element, isPrivate, showSidenav, showNavbar, requiredRoles, ...rest } = route;
                      let RouteComponent = isPrivate ? <PrivateRoute>{element}</PrivateRoute> : element;

                      if (requiredRoles && requiredRoles.length > 0) {
                        RouteComponent = <RequireRoles requiredRoles={requiredRoles}>{RouteComponent}</RequireRoles>;
                      }

                      if (showSidenav !== false && showNavbar !== false) {
                        RouteComponent = (
                          <Layout pageTitle={route.name} pageHeader={route.pageHeader || route.name}>
                            {RouteComponent}
                          </Layout>
                        );
                      }

                      return <Route key={index} {...rest} element={RouteComponent} />;
                    })}
                    <Route key={AppRoutes.length} path='*' element={<NotFound />} />
                  </Routes>
                  <ToastContainer
                    theme={theme.palette.mode}
                    style={toastStyles}
                    hideProgressBar
                    stacked
                    closeOnClick
                    icon={({ type }) => {
                      switch (type) {
                        case 'info':
                          return <InfoOutlined fontSize='medium' />;
                        case 'error':
                          return <ErrorOutlineRounded fontSize='medium' />;
                        case 'success':
                          return <TaskAltRounded fontSize='medium' />;
                        case 'warning':
                          return <WarningAmberOutlined fontSize='medium' />;
                        default:
                          return null;
                      }
                    }}
                  />
                </PromptProvider>
              </AuthProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </MaterialUIControllerProvider>
      </DndProvider>
    </AppContextProvider>
  );

  return rollbarConfig.enabled ? (
    <RollbarProvider config={rollbarConfig}>
      <ErrorBoundary>{appContent}</ErrorBoundary>
    </RollbarProvider>
  ) : (
    <ErrorBoundary>{appContent}</ErrorBoundary>
  );
};

export default App;
